import { createContext, Fragment, type ReactNode, useContext, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink } from 'react-router-dom'

export interface Crumb {
  label: string
  to?: string
}

// HeaderSlots: the places in the operator area's top bar where a page puts its
// breadcrumbs, title, actions and tabs (Martin, 2026-10-27: more room for the
// content). Without them (the project area) the page shows them itself. A
// page with tabs has its actions at the right end of the tab row, where there
// is room (Martin, 2026-10-28: beside the title they were squeezed by the
// search); registerTabs tells the top bar that tabs are there.
export interface HeaderSlots {
  crumbs: HTMLElement | null
  title: HTMLElement | null
  actions: HTMLElement | null
  tabs: HTMLElement | null
  controls: HTMLElement | null // beside the platform's state, top right
  registerTabs?: () => () => void
}
export const HeaderSlotsContext = createContext<HeaderSlots | null>(null)

// InSlot: children in a slot of the top bar, or where they are without one.
function InSlot({ slot, children }: { slot: Exclude<keyof HeaderSlots, 'registerTabs'>; children: ReactNode }) {
  const slots = useContext(HeaderSlotsContext)
  if (!slots) return <>{children}</>
  const el = slots[slot]
  return el ? createPortal(children, el) : null
}

// Crumbs: where am I.
export function Crumbs({ crumbs, className = '' }: { crumbs: Crumb[]; className?: string }) {
  return (
    <nav className={`flex flex-wrap items-center gap-1 text-xs text-muted-foreground ${className}`} aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <Fragment key={i}>
          {i > 0 && <span aria-hidden>›</span>}
          {c.to ? <Link to={c.to} className="hover:text-foreground hover:underline">{c.label}</Link> : <span>{c.label}</span>}
        </Fragment>
      ))}
    </nav>
  )
}

// HeaderCrumbs: only the breadcrumbs in the top bar (pages that bring their
// own heading).
export function HeaderCrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return <InSlot slot="crumbs"><Crumbs crumbs={crumbs} className="mb-3" /></InSlot>
}

// HeaderControls: a page's switch that belongs top right, beside the
// platform's state (Rack & network: Today / Target).
export function HeaderControls({ children }: { children: ReactNode }) {
  return <InSlot slot="controls">{children}</InSlot>
}

// PageHeader: breadcrumbs (where am I), title, description and page actions -
// in the top bar of the operator area, else at the top of the page.
export function PageHeader({ crumbs, title, badges, description, actions }: {
  crumbs?: Crumb[]; title: ReactNode; badges?: ReactNode; description?: ReactNode; actions?: ReactNode
}) {
  const slots = useContext(HeaderSlotsContext)
  if (slots) {
    return (
      <>
        {crumbs && crumbs.length > 0 && <InSlot slot="crumbs"><Crumbs crumbs={crumbs} /></InSlot>}
        <InSlot slot="title">
          <h1 className="flex flex-wrap items-center gap-2 font-display text-[20px] font-bold leading-tight text-primary">{title}{badges}</h1>
          {description && <p className="mt-0.5 line-clamp-2 max-w-5xl text-[13px] leading-snug text-muted-foreground" title={typeof description === 'string' ? description : undefined}>{description}</p>}
        </InSlot>
        {actions && <InSlot slot="actions">{actions}</InSlot>}
      </>
    )
  }
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {crumbs && crumbs.length > 0 && <Crumbs crumbs={crumbs} className="mb-1" />}
        <h1 className="flex flex-wrap items-center gap-2 font-display text-[24px] font-bold leading-tight text-primary">
          {title}
          {badges}
        </h1>
        {description && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

// RouteTabs: tabs that are part of the URL (deep links to a tab); actions sit
// at the right end of the tab row. The page's tabs go into the top bar;
// tabs inside a page section (sub-tabs) stay where they are (inline).
export function RouteTabs({ tabs, actions, inline }: { tabs: { to: string; label: string; end?: boolean }[]; actions?: ReactNode; inline?: boolean }) {
  const slots = useContext(HeaderSlotsContext)
  const inBar = !!slots && !inline
  const register = slots?.registerTabs
  // before the first paint: the page's actions move into the tab row at once
  useLayoutEffect(() => (inBar && register ? register() : undefined), [inBar, register])
  const row = (
    <div className={`flex items-center gap-1 overflow-x-auto sm:flex-wrap sm:overflow-visible ${inBar ? '' : 'mb-4 border-b'}`}>
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} replace
          className={({ isActive }) => `-mb-px mr-4 shrink-0 whitespace-nowrap border-b-[3px] px-1 py-2 text-sm font-bold ${isActive ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          {t.label}
        </NavLink>
      ))}
      {actions && <div className="ml-auto pb-1">{actions}</div>}
    </div>
  )
  return inBar ? <InSlot slot="tabs">{row}</InSlot> : row
}

export function Section({ title, actions, children, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`min-w-0 rounded-[10px] border bg-card ${className}`}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-0.5 pt-3">
          <h2 className="font-display text-[15px] font-bold">{title}</h2>
          {actions}
        </div>
      )}
      <div className="px-4 pb-4 pt-2">{children}</div>
    </section>
  )
}

// Facts: label/value pairs
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
      {items.filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
        <Fragment key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

// Buttons: one lime primary action per view; secondary buttons in petrol;
// destructive ones in red and behind a confirmation.
const btnBase = 'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-bold disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground'
export const btn = `${btnBase} border border-[#9DBBD0] bg-card text-primary hover:bg-secondary`
export const btnPrimary = `${btnBase} border border-lime-border bg-lime text-ink hover:brightness-95`
export const btnQuiet = `${btnBase} border border-transparent text-primary hover:bg-secondary`
export const btnDanger = `${btnBase} border border-[#E7A39B] bg-card text-[#A3271D] hover:bg-[#FCE4E1]`
export const input = 'px-3 py-2 border border-input rounded-md bg-card text-sm'

// WholeNumber: a field for a whole number that holds digits and nothing else. A dot or comma means
// different things in different locales, so they go as they are typed or pasted: 65.792 is 65792.
export function WholeNumber({ value, onChange, className = '' }: { value: number; onChange: (v: number) => void; className?: string }) {
  return (
    <input type="text" inputMode="numeric" autoComplete="off" className={`${input} ${className}`} value={value ? String(value) : ''}
      onChange={(e) => onChange(Number(e.target.value.replace(/\D/g, '')))} />
  )
}

// Stat: a number with a label, optionally linking to the filtered list
export function Stat({ label, value, to, tone, children }: { label: string; value: string | number; to?: string; tone?: 'crit' | 'warn'; children?: ReactNode }) {
  const color = tone === 'crit' ? 'text-[#A3271D]' : tone === 'warn' ? 'text-[#8A4B00]' : 'text-primary'
  const v = <span className={`font-display text-[24px] font-bold leading-none ${color}`}>{value}</span>
  return (
    <div className="flex flex-col gap-1.5 rounded-[10px] border bg-card px-3.5 py-3">
      <div className="text-xs font-extrabold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      {to ? <Link to={to} className="hover:underline">{v}</Link> : v}
      {children && <div>{children}</div>}
    </div>
  )
}

// Fact: one key fact in the fact strip of a detail page
export function Fact({ label, children, sub }: { label: string; children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg bg-background px-3 py-2">
      <span className="text-xs font-extrabold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words font-bold">{children}</span>
      {sub && <span className="min-w-0 break-words text-xs text-muted-foreground">{sub}</span>}
    </div>
  )
}
